import { DatePicker, Form, Input, InputNumber, Select } from 'antd'
import React, { forwardRef } from 'react'
import { SPARTACUS_PLAN_VALUES } from 'src/constants/spartacusFilters'
import {
  CATCH_BOARD_LOST_REASONS,
  CATCH_BOARD_LOST_REASON_NOTE_MAX_LENGTH,
  CATCH_BOARD_STAGE,
  getCatchBoardLostReasonLabel,
  lostReasonRequiresNote,
} from './catchBoardConstants'
import styled from 'styled-components/macro'

const { TextArea } = Input
const { Option } = Select

/**
 * Won / lost fields — shown when stage is Landed or Got Away.
 */
const CatchBoardDealClosedFieldsSection = forwardRef(
  (
    { form, onFieldChange, disabled = false, fieldErrors = {} },
    ref,
  ) => {
    const stage = form?.stage

    if (stage === CATCH_BOARD_STAGE.LANDED) {
      return (
        <Section
          ref={ref}
          data-testid='catch-board-deal-won-fields'
        >
          <SectionTitle>Landed details</SectionTitle>
          <SectionHint>
            Required when the deal is won — plan sold, MRR, and go-live date.
          </SectionHint>

          <Form.Item
            label='Plan sold'
            required
            style={{ marginBottom: 12 }}
            validateStatus={fieldErrors.wonPlan ? 'error' : undefined}
            help={fieldErrors.wonPlan || undefined}
          >
            <Select
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 160 }}
              value={form.wonPlan || undefined}
              placeholder='Select plan'
              disabled={disabled}
              onChange={value => onFieldChange('wonPlan', value || null)}
              data-testid='catch-board-deal-won-plan'
            >
              {SPARTACUS_PLAN_VALUES.map(plan => (
                <Option key={plan} value={plan}>
                  {plan}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <RowFields>
            <Form.Item
              label='Won MRR (£)'
              required
              style={{ marginBottom: 12, flex: 1 }}
              validateStatus={fieldErrors.wonMrrPounds ? 'error' : undefined}
              help={fieldErrors.wonMrrPounds || undefined}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={10}
                precision={2}
                value={form.wonMrrPounds}
                disabled={disabled}
                onChange={value =>
                  onFieldChange('wonMrrPounds', value == null ? null : value)
                }
                data-testid='catch-board-deal-won-mrr'
              />
            </Form.Item>

            <Form.Item
              label='Go-live date'
              required
              style={{ marginBottom: 12, flex: 1 }}
              validateStatus={fieldErrors.wonGoLiveDate ? 'error' : undefined}
              help={fieldErrors.wonGoLiveDate || undefined}
            >
              <DatePicker
                style={{ width: '100%' }}
                value={form.wonGoLiveDate}
                allowClear
                format='D MMM YYYY'
                disabled={disabled}
                onChange={value =>
                  onFieldChange('wonGoLiveDate', value || null)
                }
                data-testid='catch-board-deal-won-go-live'
              />
            </Form.Item>
          </RowFields>
        </Section>
      )
    }

    if (stage === CATCH_BOARD_STAGE.GOT_AWAY) {
      const needsNote = lostReasonRequiresNote(form.lostReason)

      return (
        <Section
          ref={ref}
          data-testid='catch-board-deal-lost-fields'
        >
          <SectionTitle>Got Away details</SectionTitle>
          <SectionHint>
            Required when the deal is lost — pick a reason for the team.
          </SectionHint>

          <Form.Item
            label='Lost reason'
            required
            style={{ marginBottom: 12 }}
            validateStatus={fieldErrors.lostReason ? 'error' : undefined}
            help={fieldErrors.lostReason || undefined}
          >
            <Select
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 220 }}
              value={form.lostReason || undefined}
              placeholder='Select reason'
              disabled={disabled}
              onChange={value => onFieldChange('lostReason', value || null)}
              data-testid='catch-board-deal-lost-reason'
            >
              {CATCH_BOARD_LOST_REASONS.map(reason => (
                <Option key={reason} value={reason}>
                  {getCatchBoardLostReasonLabel(reason)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {needsNote ? (
            <Form.Item
              label='Lost reason note'
              required
              style={{ marginBottom: 0 }}
              validateStatus={fieldErrors.lostReasonNote ? 'error' : undefined}
              help={fieldErrors.lostReasonNote || undefined}
            >
              <TextArea
                rows={3}
                value={form.lostReasonNote}
                maxLength={CATCH_BOARD_LOST_REASON_NOTE_MAX_LENGTH}
                disabled={disabled}
                placeholder='Tell the team what happened…'
                onChange={event =>
                  onFieldChange('lostReasonNote', event.target.value)
                }
                data-testid='catch-board-deal-lost-reason-note'
              />
            </Form.Item>
          ) : null}
        </Section>
      )
    }

    return null
  },
)

CatchBoardDealClosedFieldsSection.displayName =
  'CatchBoardDealClosedFieldsSection'

const Section = styled.div`
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const RowFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;
`

export default CatchBoardDealClosedFieldsSection
